import React, { useState } from "react";
import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Typography } from "@mui/material";
import { staffSendNotification } from "../../services/dataService";
import { logUserActivity } from "../../utils/activityLogger";

export const TITLE_MAX = 80;
export const BODY_MAX = 500;

// One free-text notification to this person (044 D32): an in-app item and a push,
// through functions/staffSendNotification.js. Admin and up, or the notifications grant
// (047 D64) — the page hides the button otherwise, and the server refuses regardless.
// Cannot be unsent, and says so.
// `noun` is the page's word for the person: "student", or "tutor" (047 step 4).
const SendNotificationDialog = ({ open, onClose, person, noun = "student", actingUser, onSent }) => {
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const t = title.trim();
  const b = body.trim();
  const valid = t.length > 0 && t.length <= TITLE_MAX && b.length > 0 && b.length <= BODY_MAX;

  const reset = () => {
    setTitle("");
    setBody("");
    setError("");
  };
  const close = () => {
    if (busy) return;
    reset();
    onClose();
  };
  const send = async () => {
    if (!valid || !person?.id) return;
    setBusy(true);
    setError("");
    try {
      const result = await staffSendNotification({ userId: person.id, title: t, body: b });
      await logUserActivity({
        userId: actingUser?.uid || actingUser?.id || "unknown",
        userName: actingUser?.name || actingUser?.displayName || "Unknown User",
        userEmail: actingUser?.email || "unknown@example.com",
        action: "SEND_NOTIFICATION",
        entityType: noun,
        entityId: person.id,
        entityName: person.name || person.id,
        after: { title: t, body: b, notificationId: result?.notificationId || null },
        description: `${actingUser?.name || "User"} sent ${person.name || person.id} a notification: ${t}`,
      });
      reset();
      onClose();
      onSent(`Notification sent to ${person.name || `the ${noun}`}.`);
    } catch (e) {
      console.error("Error sending notification:", e);
      setError(e.message || "Failed to send the notification.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={close} maxWidth="sm" fullWidth>
      <DialogTitle style={{ fontWeight: 600 }}>Send notification</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {person?.name || `This ${noun}`} gets a push and an in-app notification from 121 Islam. It cannot be unsent.
        </Typography>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <TextField
          label="Title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          fullWidth
          autoFocus
          disabled={busy}
          inputProps={{ maxLength: TITLE_MAX }}
          helperText={`${t.length}/${TITLE_MAX}`}
          sx={{ mb: 2 }}
        />
        <TextField
          label="Message"
          value={body}
          onChange={(e) => setBody(e.target.value)}
          fullWidth
          multiline
          minRows={4}
          disabled={busy}
          inputProps={{ maxLength: BODY_MAX }}
          helperText={`${b.length}/${BODY_MAX}`}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={close} disabled={busy}>Cancel</Button>
        <Button variant="contained" onClick={send} disabled={!valid || busy}>
          {busy ? "Sending…" : "Send"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SendNotificationDialog;
